import { useState, useEffect, useRef, useCallback } from 'react';
import { RiskMeterDto } from '../../../../dtos/RiskMeterDto';
import { GrantFinancialDto } from '../../../../dtos/GrantFinancialDto';
import { RiskMeterProps, RiskMeterDisplayProps } from '../types/riskMeterTypes';
import { getRiskMeterData } from '../../../../api/v2/RiskMeterApi';
import { getGrantFinancial } from '../../../../api/v2/GrantFinancialApi';
import { useRiskMeterExport } from './useRiskMeterExport';

interface UseRiskMeterControllerResult extends RiskMeterDisplayProps {
    data: RiskMeterDto | null;
    isLoading: boolean;
    error: string | null;
    screenshotTitle?: string;
    containerRef: React.RefObject<HTMLDivElement>;
    exportAsPng: () => Promise<void>;
    grantId?: string;
    financial: GrantFinancialDto | null;
    reload: () => void;
}

export function useRiskMeterController(props: RiskMeterProps): UseRiskMeterControllerResult {
    const [data, setData] = useState<RiskMeterDto | null>(props.data ?? null);
    const [financial, setFinancial] = useState<GrantFinancialDto | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(!props.data && !!props.grantId);
    const [error, setError] = useState<string | null>(null);
    // Bumped after an edit so the meter refetches even when the parent supplied the data
    const [reloadKey, setReloadKey] = useState(0);
    const hasLoadedRef = useRef(false);
    const { containerRef, exportAsPng } = useRiskMeterExport(props.screenshotTitle, props.screenshotFilename);

    const reload = useCallback(() => setReloadKey(key => key + 1), []);

    useEffect(() => {
        // If data is provided directly, use it - unless an edit asked for a fresh read
        if (props.data && reloadKey === 0) {
            setData(props.data);
            setIsLoading(false);
            hasLoadedRef.current = true;
            return;
        }

        // If no grantId, nothing to fetch
        if (!props.grantId) {
            setIsLoading(false);
            return;
        }

        const fetchData = async () => {
            // Only show loading on initial fetch
            if (!hasLoadedRef.current) {
                setIsLoading(true);
            }
            setError(null);

            try {
                const result = await getRiskMeterData(props.grantId!);
                if (result?.data) {
                    setData(result.data);
                    hasLoadedRef.current = true;
                } else if (result?.error) {
                    setError(result.error.message);
                }
            } catch (e) {
                setError('Failed to load risk meter data');
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [props.grantId, props.data, props.refreshKey, reloadKey]);

    // Financial edit state is only needed by the full view, which is the only place with a grantId
    useEffect(() => {
        if (!props.grantId || !props.showTable) {
            setFinancial(null);
            return;
        }

        const fetchFinancial = async () => {
            const result = await getGrantFinancial(props.grantId!);
            setFinancial(result?.data ?? null);
        };

        fetchFinancial();
    }, [props.grantId, props.showTable, props.refreshKey, reloadKey]);

    return {
        data,
        isLoading,
        error,
        screenshotTitle: props.screenshotTitle,
        containerRef,
        exportAsPng,
        grantId: props.grantId,
        financial,
        reload,
        variant: props.variant,
        height: props.height,
        width: props.width,
        showLabels: props.showLabels,
        showConnectors: props.showConnectors,
        showTooltip: props.showTooltip,
        showCenterCircle: props.showCenterCircle,
        showBackground: props.showBackground,
        showTable: props.showTable,
        showLegend: props.showLegend
    };
}
